import type { RequestReadyForCheckReportSummary } from "../components/RequestReadyForCheckDialog"
import type { HourReportAdminAction, HourReportDayAuditAdminAction } from "../../../hour-reports/api"
import type { TreeGridApprovedHourDateGroupRow, TreeGridApprovedHourRow } from "../rows/types"
import { isTotalHoursRow, isHourReportRow, isDateGroupRow, getDateGroupRows } from "../rows/utils"

type HandlerParams = {
  grid: TGrid
  row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow
  checked: boolean
  oldValue: unknown
  requestReadyForCheck: (row: RequestReadyForCheckReportSummary) => void
  checkHourReport: (args: { id: string; status: "submitted" | "checked" }) => unknown
  performHourReportAdminAction: (args: { id: string; action: HourReportAdminAction }) => unknown
  performDayAuditAdminAction: (args: { days: string[]; action: HourReportDayAuditAdminAction }) => unknown
  setDaysReadyForApproval: (args: {
    days: string[]
    status: "submitted" | "checked"
    notifyApprover?: boolean
  }) => unknown
  confirmAllReportsReadyForApproval: (request: TotalReadyForApprovalRequest) => void
}

export type TotalReadyForApprovalRequest = {
  days: string[]
  reportCount: number
}

const isDraftReportCheckAttempt = (
  row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow,
  checked: boolean
) => {
  return isHourReportRow(row) && checked && row.status === "open"
}

export const handleChangeReadyForApproval = (params: HandlerParams) => {
  const {
    row,
    checked,
    requestReadyForCheck,
    checkHourReport,
    performHourReportAdminAction,
    performDayAuditAdminAction,
    setDaysReadyForApproval,
    confirmAllReportsReadyForApproval,
  } = params
  const nextStatus = checked ? "checked" : "submitted"

  if (isHourReportRow(row) && row.editableAs === "hour-report-admin") {
    return performHourReportAdminAction({
      id: row.id,
      action: checked ? "check" : "uncheck",
    })
  }

  if (isDraftReportCheckAttempt(row, checked)) {
    requestReadyForCheck(row)
    return checked ? 0 : 1
  }

  if (isHourReportRow(row)) {
    return checkHourReport({
      id: row.id,
      status: nextStatus,
    })
  }

  if (isDateGroupRow(row)) {
    if (row.canManageAsHourReportAdmin) {
      return performDayAuditAdminAction({
        days: [row.id],
        action: checked ? "check" : "uncheck",
      })
    }

    return setDaysReadyForApproval({
      days: [row.id],
      status: nextStatus,
    })
  }

  if (isTotalHoursRow(row)) {
    const dateRows = getDateGroupRows(params.grid)
    const dateRowIds = dateRows.map((row) => row.id)
    const canManageAsAdmin = dateRows.some((row) => row.canManageAsHourReportAdmin)
    if (!dateRowIds.length) return

    if (canManageAsAdmin) {
      return performDayAuditAdminAction({
        days: dateRowIds,
        action: checked ? "check" : "uncheck",
      })
    }

    if (checked) {
      confirmAllReportsReadyForApproval({
        days: dateRowIds,
        reportCount: dateRows.reduce((total, row) => total + getReportCount(row), 0),
      })
      return 0
    }

    return setDaysReadyForApproval({
      days: dateRowIds,
      status: nextStatus,
    })
  }
}

const getReportCount = (row: TreeGridApprovedHourDateGroupRow) => {
  const treeGridRow = row as TreeGridApprovedHourDateGroupRow & { childNodes?: unknown[] }
  return treeGridRow.Items?.length ?? treeGridRow.childNodes?.length ?? 0
}
